import { summarizeErrors } from './member-form-errors';

const PHOTO_EXTENSIONS = ['avif', 'heic', 'jpeg', 'jpg', 'png', 'webp'];
const PHOTO_MIME_TYPES = [
  'image/avif',
  'image/heic',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

export default (config = {}) => ({
  page: config.initialPage || 1,
  submitting: false,
  validatingStep: false,
  skippingStep: false,
  showApplicationStatus: true,
  dirtySteps: [],
  country: config.country || 'United States',
  state: config.state || '',
  usStates: Array.isArray(config.usStates) ? config.usStates : [],
  canadianProvinces: Array.isArray(config.canadianProvinces) ? config.canadianProvinces : [],
  newsroom: Boolean(config.newsroom),
  selectedSkills: Array.isArray(config.selectedSkills) ? config.selectedSkills : [],
  spokenLanguages: Array.isArray(config.spokenLanguages) ? config.spokenLanguages : [],
  additionalLanguageInfo: config.additionalLanguageInfo || '',
  displayProfileInformationPublicly: Boolean(config.displayProfileInformationPublicly),
  displayEmailAddress: Boolean(config.displayEmailAddress),
  headline: config.headline || '',
  vanitySlug: config.vanitySlug || '',
  featuredWork: [],
  awardsCertificates: [],
  photoDragOver: false,
  persistedPhotoUrl: config.profilePhotoUrl || '',
  photoPreview: config.profilePhotoUrl || '',
  photoError: '',
  rowKey: 0,
  beforeUnloadHandler: null,

  get dirty() {
    return this.dirtySteps.length > 0;
  },

  get subdivisionOptions() {
    if (this.country === 'United States') {
      return this.usStates;
    }
    if (this.country === 'Canada') {
      return this.canadianProvinces;
    }
    return [];
  },

  get hasSubdivisionOptions() {
    return this.subdivisionOptions.length > 0;
  },

  get subdivisionLabel() {
    return this.country === 'Canada' ? 'Province / Territory' : 'State';
  },

  get subdivisionPlaceholder() {
    return this.country === 'Canada' ? 'Select a province or territory' : 'Select a state';
  },

  get hasAdditionalLanguages() {
    return this.spokenLanguages.includes('other');
  },

  init() {
    this.featuredWork = this.normalizeRows(config.featuredWork, [
      'videoYoutubeUrl',
      'videoTitle',
      'videoDescription',
    ]);
    this.awardsCertificates = this.normalizeRows(config.awardsCertificates, [
      'awardCertificateName',
    ]);

    this.beforeUnloadHandler = (event) => {
      if (!this.dirty || this.submitting) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    if (config.hasErrors) {
      this.$nextTick(() => {
        // Twig cannot place errors inside rows that Alpine creates with x-for, so replay only
        // nested errors after those rows exist. Regular field errors remain server-rendered.
        const nestedErrors = Object.fromEntries(
          Object.entries(config.initialErrors || {}).filter(([attribute]) => attribute.includes('.')),
        );
        this.$root.querySelectorAll('[data-registration-step]').forEach((panel) => {
          this.renderStepErrors(panel, nestedErrors, false);
        });
        this.focusFirstInvalid();
      });
    }
  },

  destroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  },

  markDirty() {
    if (!this.submitting && !this.dirtySteps.includes(this.page)) {
      this.dirtySteps.push(this.page);
    }
  },

  async goToPage(nextPage, validateCurrent = true) {
    if (this.validatingStep || this.submitting) {
      return;
    }

    // Forward buttons validate and save the current step. Back passes false so applicants
    // can review previous answers without being blocked by the page they are leaving.
    if (validateCurrent && !await this.validateStep(this.page)) {
      return;
    }

    this.showPage(nextPage);
  },

  async skipStep(step, nextPage) {
    if (this.validatingStep || this.submitting) {
      return;
    }

    // Persist the progress transition so another session resumes after the skipped step.
    this.skippingStep = true;
    try {
      if (!await this.validateStep(step, true)) {
        return;
      }
    } finally {
      this.skippingStep = false;
    }

    this.showPage(nextPage);
  },

  showPage(nextPage) {
    this.page = nextPage;
    this.$nextTick(() => {
      this.scrollToPageTop();

      // Wait for the newly visible panel to render before moving focus. Otherwise,
      // hiding the clicked navigation button can return focus to the document body.
      window.requestAnimationFrame(() => {
        const heading = this.$root.querySelector(`#registration-step-${nextPage}-title`);
        heading?.focus({preventScroll: true});
      });
    });
  },

  async submitForm(event) {
    event.preventDefault();

    if (this.submitting || this.validatingStep) {
      return;
    }

    if (this.page < 3) {
      // Treat an implicit form submission on an earlier step, such as pressing Enter in an
      // input, as Next rather than attempting account creation from an incomplete form.
      await this.goToPage(this.page + 1);
      return;
    }

    if (!config.finalizationEndpoint) {
      this.renderApplicationErrors({
        form: ['We couldn’t complete your application. Please refresh the page and try again.'],
      });
      return;
    }

    await this.finalizeApplication();
  },

  async finalizeApplication() {
    const panels = Array.from(this.$root.querySelectorAll('[data-registration-step]'));
    panels.forEach((panel) => this.clearStepErrors(panel));
    this.submitting = true;
    let redirecting = false;

    // The finalization endpoint assembles earlier steps from Craft. Send only Step 3 so stale
    // browser state cannot overwrite application data that was already saved and validated.
    const finalStepAttributes = new Set(
      Array.isArray(config.finalStepAttributes) ? config.finalStepAttributes : [],
    );
    const formData = new FormData(this.$root);
    formData.delete('action');
    formData.delete('redirect');
    Array.from(formData.keys()).forEach((key) => {
      const attribute = key.match(/^registration\[([^\]]+)]/)?.[1];
      if (attribute && !finalStepAttributes.has(attribute)) {
        formData.delete(key);
      }
    });

    try {
      const response = await fetch(config.finalizationEndpoint, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      const result = await response.json();

      if (typeof result?.valid !== 'boolean' || typeof result?.completed !== 'boolean') {
        throw new Error('Invalid finalization response.');
      }

      if (!response.ok || !result.valid || !result.completed) {
        this.renderApplicationErrors(Object.keys(result.errors || {}).length
          ? result.errors
          : {form: ['We couldn’t complete your application. Please try again.']});
        return;
      }

      if (typeof result.redirectUrl !== 'string' || result.redirectUrl === '') {
        throw new Error('Finalization response did not include a redirect.');
      }

      redirecting = true;
      this.dirtySteps = [];
      window.location.assign(result.redirectUrl);
    } catch (error) {
      this.renderApplicationErrors({
        form: ['We couldn’t complete your application. Please try again.'],
      });
    } finally {
      // Keep the submit controls disabled while the successful navigation is in progress.
      if (!redirecting) {
        this.submitting = false;
      }
    }
  },

  async validateStep(step, skip = false) {
    const panel = this.$root.querySelector(`[data-registration-step="${step}"]`);
    const endpoint = config.saveEndpoint;
    if (!panel || !endpoint) {
      return false;
    }

    this.validatingStep = true;
    this.clearStepErrors(panel);

    // Serialize the real form rather than rebuilding its payload in JavaScript. This keeps
    // checkbox fallbacks, conditional inputs, and indexed repeater names identical to Submit.
    const formData = new FormData(this.$root);
    // The endpoint URL selects the step action; these final-submit controls are irrelevant
    // to a step request and could otherwise make the request's intent ambiguous.
    formData.delete('action');
    formData.delete('redirect');
    formData.set('step', String(step));

    if (skip) {
      // A skip is a progress-only transition. Excluding every registration field keeps that
      // contract explicit in the request as well as enforced by the backend.
      Array.from(formData.keys()).forEach((key) => {
        if (key.startsWith('registration[')) {
          formData.delete(key);
        }
      });
      formData.set('skip', '1');
    } else {
      // Application saves never include account credentials. Complex fields are sent only to
      // the step that owns them so another step cannot replace persisted photos or table rows.
      formData.delete('registration[email]');
      formData.delete('registration[password]');
      if (step !== 1) {
        formData.delete('registration[profilePhoto]');
      }
      if (step !== 2) {
        Array.from(formData.keys()).forEach((key) => {
          if (
            key.startsWith('registration[featuredWork][') ||
            key.startsWith('registration[awardsCertificates][')
          ) {
            formData.delete(key);
          }
        });
      }
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      const result = await response.json();
      const failureMessage = 'We couldn’t save this step. Please try again.';

      if (typeof result?.valid !== 'boolean') {
        throw new Error('Invalid step response.');
      }

      if (!response.ok) {
        this.renderStepErrors(panel, Object.keys(result.errors || {}).length
          ? result.errors
          : {form: [failureMessage]});
        return false;
      }

      if (!result.valid) {
        this.renderStepErrors(panel, Object.keys(result.errors || {}).length
          ? result.errors
          : {form: [failureMessage]});
        return false;
      }

      // A valid response is incomplete unless the backend also confirms the step was saved.
      if (result.saved !== true) {
        throw new Error('Step was validated but not saved.');
      }
      if (skip && result.skipped !== true) {
        throw new Error('Step was saved without confirming the skip.');
      }

      if (step === 1) {
        // Keep the local preview as a short-lived fallback if an asset transform URL is not
        // immediately available. Reloading the page always hydrates Craft's persisted URL.
        this.commitSavedPhoto(
          typeof result.profilePhotoUrl === 'string' && result.profilePhotoUrl !== ''
            ? result.profilePhotoUrl
            : this.photoPreview,
        );
      }

      // Clear only the saved step. Applicants can go Back with unsaved answers on another
      // step, and those changes must continue to trigger the unload warning.
      this.dirtySteps = this.dirtySteps.filter((dirtyStep) => dirtyStep !== step);

      // All steps share this notice state. Dismiss it only after persistence is confirmed,
      // so failed saves keep the context visible and Back cannot restore a stale notice.
      this.showApplicationStatus = false;

      return true;
    } catch (error) {
      this.renderStepErrors(panel, {
        form: ['We couldn’t save this step. Please try again.'],
      });
      return false;
    } finally {
      this.validatingStep = false;
    }
  },

  clearStepErrors(panel) {
    // A step may be checked repeatedly. Remove its previous messages and their ARIA references
    // before rendering the latest authoritative response so errors never accumulate or go stale.
    panel.querySelector('[data-member-error-summary]')?.remove();
    panel.querySelectorAll('.fui-error-messages').forEach((errorContainer) => {
      const errorId = errorContainer.id;
      if (errorId) {
        panel.querySelectorAll('[aria-describedby]').forEach((control) => {
          const ids = control.getAttribute('aria-describedby').split(/\s+/).filter((id) => id !== errorId);
          if (ids.length) {
            control.setAttribute('aria-describedby', ids.join(' '));
          } else {
            control.removeAttribute('aria-describedby');
          }
        });
      }
      errorContainer.remove();
    });
    panel.querySelectorAll('[aria-invalid="true"]').forEach((control) => control.removeAttribute('aria-invalid'));
  },

  renderStepErrors(panel, errors, focusInvalid = true) {
    Object.entries(errors).forEach(([attribute, messages]) => {
      const attributePath = attribute.split('.');
      const rootAttribute = attributePath.shift();
      const rootField = Array.from(panel.querySelectorAll('[data-field-handle]'))
        .find((candidate) => candidate.dataset.fieldHandle === rootAttribute);
      // Convert a backend path such as featuredWork.0.videoTitle to the submitted input name.
      const expectedName = `registration[${rootAttribute}]${attributePath.map((part) => `[${part}]`).join('')}`;
      // Lightswitches and checkbox groups submit a hidden empty-value fallback before their
      // interactive inputs. Errors must describe and focus the control the applicant can use.
      const control = Array.from(panel.querySelectorAll('input:not([type="hidden"]), select, textarea'))
        .find((candidate) => attributePath.length
          ? candidate.name === expectedName
          : candidate.name === expectedName || candidate.name.startsWith(`${expectedName}[`));
      // Ordinary fields use their data-field-handle wrapper. Nested repeater errors instead use
      // the closest field around the exact indexed control so the message appears beneath it.
      const field = attributePath.length ? control?.closest('.fui-field') : rootField;
      // Errors not owned by a field are intentionally rendered only for the model-level `form`
      // key. Errors from another hidden step are ignored by this panel.
      const target = field || (attribute === 'form' ? panel.querySelector('.row-wrapper') : null);
      if (!target || !Array.isArray(messages) || messages.length === 0) {
        return;
      }

      // Dots are useful in the backend path but normalize them for predictable HTML IDs.
      const errorId = `registration-${attribute.replace(/[^a-zA-Z0-9_-]+/g, '-')}-errors`;
      const errorContainer = document.createElement('div');
      errorContainer.id = errorId;
      errorContainer.className = 'fui-error-messages';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.setAttribute('tabindex', '-1');

      messages.forEach((message) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'fui-error-message';
        paragraph.textContent = message;
        errorContainer.append(paragraph);
      });
      if (field) {
        target.append(errorContainer);
      } else {
        target.prepend(errorContainer);
      }

      if (!field || !control) {
        return;
      }

      control.setAttribute('aria-invalid', 'true');
      const describedBy = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(errorId);
      control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    });

    // Twig already places the resume notice before the fields. Position the
    // dynamically created summary after it each time validation errors change.
    summarizeErrors(panel, panel.querySelector('[data-incomplete-application-status]'));

    // Server validators are grouped by rule, so use DOM order to focus the first
    // invalid field as the applicant encounters it visually.
    const focusTarget = focusInvalid ? panel.querySelector('[aria-invalid="true"], .fui-error-messages') : null;
    if (focusTarget) {
      focusTarget.focus({preventScroll: true});
      focusTarget.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  },

  renderApplicationErrors(errors) {
    const panels = Array.from(this.$root.querySelectorAll('[data-registration-step]'));
    const errorsByStep = {1: {}, 2: {}, 3: {}};

    Object.entries(errors).forEach(([attribute, messages]) => {
      const rootAttribute = attribute.split('.')[0];
      // Model-level and unexpected errors belong on the final step. Known field errors are
      // routed by the existing data-field-handle markup instead of duplicating field maps in JS.
      const ownerPanel = attribute === 'form'
        ? null
        : panels.find((panel) => Array.from(panel.querySelectorAll('[data-field-handle]'))
          .some((field) => field.dataset.fieldHandle === rootAttribute));
      const step = Number(ownerPanel?.dataset.registrationStep) || 3;
      const key = ownerPanel ? attribute : 'form';
      errorsByStep[step][key] = [...(errorsByStep[step][key] || []), ...messages];
    });

    panels.forEach((panel) => {
      const step = Number(panel.dataset.registrationStep);
      this.renderStepErrors(panel, errorsByStep[step] || {}, false);
    });

    const firstInvalidStep = [1, 2, 3]
      .find((step) => Object.keys(errorsByStep[step]).length > 0) || 3;
    this.page = firstInvalidStep;
    this.$nextTick(() => {
      // Wait one rendered frame after x-show reveals the owning step. Focusing while its
      // controls are still display:none causes browsers to leave focus on the document body.
      window.requestAnimationFrame(() => {
        const panel = this.$root.querySelector(`[data-registration-step="${firstInvalidStep}"]`);
        const focusTarget = panel?.querySelector('[aria-invalid="true"], .fui-error-messages');
        if (focusTarget) {
          focusTarget.focus({preventScroll: true});
          focusTarget.scrollIntoView({behavior: 'smooth', block: 'center'});
        } else {
          this.scrollToPageTop();
        }
      });
    });
  },

  validateCheckboxGroup(group) {
    if (!group) {
      return true;
    }

    const checkboxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
    const firstCheckbox = checkboxes[0];
    if (!firstCheckbox) {
      return true;
    }

    const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length;
    const minimum = Number(group.dataset.minimum || 0);
    const maximum = Number(group.dataset.maximum || 0);
    let message = '';

    if (minimum && checkedCount < minimum) {
      message = `Select at least ${minimum} option${minimum === 1 ? '' : 's'}.`;
    } else if (maximum && checkedCount > maximum) {
      message = `Select no more than ${maximum} options.`;
    }

    firstCheckbox.setCustomValidity(message);
    return message === '';
  },

  handleCountryChange() {
    // A subdivision code only has meaning within its selected country.
    this.state = '';
  },

  handleSpokenLanguagesChange() {
    if (!this.hasAdditionalLanguages) {
      // Do not submit a hidden value after the controlling Other option is removed.
      this.additionalLanguageInfo = '';
    }
  },

  handleProfileVisibilityChange() {
    if (!this.displayProfileInformationPublicly) {
      // A hidden profile cannot expose its email independently.
      this.displayEmailAddress = false;
    }
  },

  addFeaturedWork() {
    if (this.featuredWork.length >= 4) {
      return;
    }

    this.featuredWork.push(this.createRow([
      'videoYoutubeUrl',
      'videoTitle',
      'videoDescription',
    ]));
    this.markDirty();
  },

  removeFeaturedWork(index) {
    this.featuredWork.splice(index, 1);
    this.markDirty();
  },

  addAward() {
    this.awardsCertificates.push(this.createRow(['awardCertificateName']));
    this.markDirty();
  },

  removeAward(index) {
    this.awardsCertificates.splice(index, 1);
    this.markDirty();
  },

  normalizeRows(rows, fields) {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row) => {
      const normalized = this.createRow(fields);
      fields.forEach((field) => {
        normalized[field] = typeof row?.[field] === 'string' ? row[field] : '';
      });
      return normalized;
    });
  },

  createRow(fields) {
    const row = {key: `registration-row-${this.rowKey++}`};
    fields.forEach((field) => {
      row[field] = '';
    });
    return row;
  },

  handlePhotoChange(event) {
    const file = event.target.files?.[0];
    this.setPhoto(file);
  },

  handlePhotoDrop(event) {
    this.photoDragOver = false;
    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }

    if (files.length > 1) {
      this.setPhotoError('Please upload one profile photo.');
      return;
    }

    const file = files[0];
    if (!this.validatePhoto(file)) {
      return;
    }

    try {
      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.$refs.profilePhoto.files = transfer.files;
    } catch (error) {
      this.setPhotoError('Your browser could not attach the dropped image. Please choose the file instead.');
      return;
    }
    this.setPhoto(file);
    this.markDirty();
  },

  setPhoto(file) {
    if (!file) {
      this.photoPreview = this.persistedPhotoUrl;
      this.setPhotoError('');
      return;
    }

    if (!this.validatePhoto(file)) {
      this.$refs.profilePhoto.value = '';
      this.photoPreview = this.persistedPhotoUrl;
      return;
    }

    this.setPhotoError('');
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      this.photoPreview = reader.result;
    }, {once: true});
    reader.readAsDataURL(file);
  },

  commitSavedPhoto(url) {
    this.persistedPhotoUrl = url;
    // Retain the local data preview after replacement so a same-URL Craft asset does not
    // briefly show a browser-cached previous image. Reloads hydrate the authoritative URL.
    if (!this.photoPreview) {
      this.photoPreview = url;
    }
    this.$refs.profilePhoto.value = '';
    this.setPhotoError('');
  },

  validatePhoto(file) {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const validExtension = PHOTO_EXTENSIONS.includes(extension);
    const validMimeType = file.type === '' || PHOTO_MIME_TYPES.includes(file.type);

    if (!validExtension || !validMimeType) {
      this.setPhotoError('Invalid file type. Please upload an AVIF, HEIC, JPEG, PNG, or WebP image.');
      return false;
    }

    if (file.size > MAX_PHOTO_SIZE) {
      this.setPhotoError('File size too large. Please upload an image smaller than 5 MB.');
      return false;
    }

    return true;
  },

  setPhotoError(message) {
    this.photoError = message;
    this.$refs.profilePhoto?.setCustomValidity(message);
  },

  focusFirstInvalid() {
    const panel = this.$root.querySelector(`[data-registration-step="${this.page}"]`);
    const invalidField = panel?.querySelector('[aria-invalid="true"], :invalid');
    invalidField?.focus({preventScroll: true});
    invalidField?.scrollIntoView({behavior: 'smooth', block: 'center'});
  },

  scrollToPageTop() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  },

  scrollToForm() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.$root.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  },
});
